import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const TABS = [
  { key: 'users', label: '계정 관리' },
  { key: 'system', label: '시스템 모니터링' },
  { key: 'apikeys', label: 'API 키 관리' },
];

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div>
      <h1 style={S.h1}>관리자</h1>
      <p style={S.desc}>계정 관리, 시스템 모니터링, API 키 관리</p>

      <div style={S.tabBar}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ ...S.tabBtn, ...(activeTab === tab.key ? S.tabBtnActive : {}) }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'system' && <SystemTab />}
      {activeTab === 'apikeys' && <ApiKeysTab />}
    </div>
  );
}

/* ═══════ 계정 관리 탭 ═══════ */
function UsersTab() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ username: '', password: '', role: '' });

  const loadUsers = useCallback(async () => {
    try {
      const r = await api.get('/admin/users');
      setUsers(r.data.users || []);
    } catch (e) { setError(e.response?.data?.error || '사용자 목록 로드 실패'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const addUser = async () => {
    if (!newUser.username || !newUser.password) { setError('아이디와 비밀번호를 입력하세요'); return; }
    try {
      await api.post('/admin/users', newUser);
      setNewUser({ username: '', password: '', role: 'user' });
      setShowAdd(false);
      await loadUsers();
    } catch (e) { setError(e.response?.data?.error || '사용자 추가 실패'); }
  };

  const startEdit = (user) => {
    setEditId(user._id);
    setEditData({ username: user.username, password: '', role: user.role });
  };

  const saveEdit = async () => {
    try {
      const payload = { username: editData.username, role: editData.role };
      if (editData.password) payload.password = editData.password;
      await api.put(`/admin/users/${editId}`, payload);
      setEditId(null);
      await loadUsers();
    } catch (e) { setError(e.response?.data?.error || '수정 실패'); }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`"${username}" 사용자를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      await loadUsers();
    } catch (e) { setError(e.response?.data?.error || '삭제 실패'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ko-KR') : '';

  if (loading) return <div style={S.center}><div style={S.spinner} /></div>;

  return (
    <div>
      {error && <div style={S.errorBox}>{error}<button style={S.errorClose} onClick={() => setError('')}>✕</button></div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: '#8B949E', fontSize: 14 }}>총 {users.length}명</span>
        <button style={S.primaryBtn} onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? '취소' : '+ 사용자 추가'}
        </button>
      </div>

      {/* 사용자 추가 폼 */}
      {showAdd && (
        <div style={S.addForm}>
          <input value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })}
            placeholder="아이디" style={S.input} />
          <input value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })}
            placeholder="비밀번호" type="password" style={S.input} />
          <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} style={S.select}>
            <option value="user">일반</option>
            <option value="manager">관리자</option>
            <option value="admin">총관리자</option>
          </select>
          <button style={S.primaryBtn} onClick={addUser}>추가</button>
        </div>
      )}

      {/* 사용자 테이블 */}
      <div style={S.table}>
        <div style={S.tableHeader}>
          <span style={{ flex: 2 }}>아이디</span>
          <span style={{ flex: 1 }}>역할</span>
          <span style={{ flex: 0.8 }}>토큰</span>
          <span style={{ flex: 1 }}>가입일</span>
          <span style={{ flex: 1.5, textAlign: 'right' }}>관리</span>
        </div>
        {users.map(user => (
          <div key={user._id} style={S.tableRow}>
            {editId === user._id ? (
              <>
                <input value={editData.username} onChange={e => setEditData({ ...editData, username: e.target.value })} style={{ ...S.input, flex: 2 }} />
                <select value={editData.role} onChange={e => setEditData({ ...editData, role: e.target.value })} style={{ ...S.select, flex: 1 }}>
                  <option value="user">일반</option><option value="manager">관리자</option><option value="admin">총관리자</option>
                </select>
                <input value={editData.password} onChange={e => setEditData({ ...editData, password: e.target.value })}
                  placeholder="새 비밀번호 (선택)" type="password" style={{ ...S.input, flex: 1 }} />
                <div style={{ flex: 1.5, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button style={S.smallBtnGreen} onClick={saveEdit}>저장</button>
                  <button style={S.smallBtn} onClick={() => setEditId(null)}>취소</button>
                </div>
              </>
            ) : (
              <>
                <span style={{ flex: 2, color: '#E6EDF3', fontWeight: 500 }}>{user.username}</span>
                <span style={{ flex: 1 }}>
                  <span style={{ ...S.roleBadge, backgroundColor: user.role === 'admin' ? '#C62828' : user.role === 'manager' ? '#F57F17' : '#374151' }}>
                    {user.role === 'admin' ? '총관리자' : user.role === 'manager' ? '관리자' : '일반'}
                  </span>
                </span>
                <span style={{ flex: 0.8, color: (user.role === 'admin' || user.role === 'manager') ? '#00F0FF' : '#8B949E', fontSize: 13, fontWeight: 600 }}>
                  {(user.role === 'admin' || user.role === 'manager') ? '♾️' : (user.tokens ?? 0).toLocaleString()}
                </span>
                <span style={{ flex: 1, color: '#8B949E', fontSize: 13 }}>{formatDate(user.createdAt)}</span>
                <div style={{ flex: 1.5, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button style={S.smallBtn} onClick={() => startEdit(user)}>수정</button>
                  {user.username !== 'admin' && (
                    <button style={S.smallBtnDanger} onClick={() => deleteUser(user._id, user.username)}>삭제</button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════ 시스템 모니터링 탭 ═══════ */
function SystemTab() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get('/admin/stats');
      setStats(r.data);
    } catch {}
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes > 1024 ** 3) return (bytes / 1024 ** 3).toFixed(1) + ' GB';
    if (bytes > 1024 ** 2) return (bytes / 1024 ** 2).toFixed(1) + ' MB';
    return (bytes / 1024).toFixed(1) + ' KB';
  };

  const formatUptime = (sec) => {
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${d}일 ${h}시간 ${m}분`;
  };

  if (loading) return <div style={S.center}><div style={S.spinner} /></div>;
  if (!stats) return <div style={{ color: '#8B949E' }}>데이터 로드 실패</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button style={S.ghostBtn} onClick={() => { setLoading(true); loadStats(); }}>새로고침</button>
      </div>

      {/* 서버 정보 */}
      <div style={S.statGrid}>
        <div style={S.statCard}>
          <div style={S.statLabel}>서버 가동시간</div>
          <div style={S.statValue}>{formatUptime(stats.server.uptime)}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>Node.js</div>
          <div style={S.statValue}>{stats.server.nodeVersion}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>플랫폼</div>
          <div style={S.statValue}>{stats.server.platform} / {stats.server.arch}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>사용자 수</div>
          <div style={S.statValue}>{stats.users}명</div>
        </div>
      </div>

      {/* 메모리 */}
      <h3 style={S.sectionTitle}>메모리</h3>
      <div style={S.card}>
        <div style={S.barLabel}>
          <span>사용 {formatBytes(stats.memory.used)}</span>
          <span>{stats.memory.usagePercent}%</span>
        </div>
        <div style={S.barWrap}>
          <div style={{
            ...S.barFill,
            width: `${stats.memory.usagePercent}%`,
            backgroundColor: stats.memory.usagePercent > 80 ? '#EF4444' : '#4CAF50',
          }} />
        </div>
        <div style={S.barFooter}>
          <span>전체: {formatBytes(stats.memory.total)}</span>
          <span>여유: {formatBytes(stats.memory.free)}</span>
        </div>
      </div>

      {/* CPU */}
      <h3 style={S.sectionTitle}>CPU</h3>
      <div style={S.card}>
        <div style={{ color: '#E6EDF3', fontSize: 14, marginBottom: 8 }}>{stats.cpu.model}</div>
        <div style={{ color: '#8B949E', fontSize: 13 }}>코어: {stats.cpu.cores} | Load Avg: {stats.cpu.loadAvg.join(' / ')}</div>
      </div>

      {/* 디스크 (/data) */}
      <h3 style={S.sectionTitle}>디스크 (/data)</h3>
      <div style={S.card}>
        {stats.disk.total > 0 ? (
          <>
            <div style={S.barLabel}>
              <span>사용 {formatBytes(stats.disk.used)}</span>
              <span>{Math.round((stats.disk.used / stats.disk.total) * 100)}%</span>
            </div>
            <div style={S.barWrap}>
              <div style={{ ...S.barFill, width: `${Math.round((stats.disk.used / stats.disk.total) * 100)}%` }} />
            </div>
            <div style={S.barFooter}>
              <span>전체: {formatBytes(stats.disk.total)}</span>
              <span>여유: {formatBytes(stats.disk.free)}</span>
            </div>
          </>
        ) : (
          <div style={{ color: '#8B949E', fontSize: 13 }}>디스크 정보를 가져올 수 없습니다</div>
        )}
      </div>

      {/* 저장소 상세 */}
      <h3 style={S.sectionTitle}>저장소 사용량</h3>
      <div style={S.statGrid}>
        <div style={S.statCard}>
          <div style={S.statLabel}>업로드 (uploads)</div>
          <div style={S.statValue}>{formatBytes(stats.storage.uploads)}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>출력 (outputs)</div>
          <div style={S.statValue}>{formatBytes(stats.storage.outputs)}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>디스크 여유</div>
          <div style={S.statValue}>{formatBytes(stats.disk.free)}</div>
        </div>
      </div>
    </div>
  );
}

/* ═══════ API 키 관리 탭 ═══════ */
function ApiKeysTab() {
  const keys = [
    { name: 'ANTHROPIC_API_KEY', label: 'Claude API', desc: '대본 생성, 메타데이터 생성에 사용', required: true },
    { name: 'YOUTUBE_API_KEY', label: 'YouTube Data API v3', desc: '키워드 분석 시 유튜브 데이터 조회', required: false },
    { name: 'NAVER_CLIENT_ID', label: 'Naver Client ID', desc: '네이버 DataLab 검색 트렌드 조회', required: false },
    { name: 'NAVER_CLIENT_SECRET', label: 'Naver Client Secret', desc: '네이버 DataLab API 인증', required: false },
    { name: 'GOOGLE_API_KEY', label: 'Google API Key', desc: 'Google 서비스 연동', required: false },
  ];

  return (
    <div>
      <div style={{ ...S.card, marginBottom: 16, backgroundColor: 'rgba(76,175,80,0.08)', borderColor: 'rgba(76,175,80,0.25)' }}>
        <p style={{ color: '#C9D1D9', fontSize: 13, margin: 0, lineHeight: 1.6 }}>
          API 키는 서버의 <code style={{ color: '#4CAF50' }}>.env</code> 파일에서 직접 관리합니다.
          보안을 위해 웹에서는 키 값을 표시하지 않으며, 설정 상태만 확인할 수 있습니다.
          키 변경 후 서버를 재시작하세요.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {keys.map(k => (
          <div key={k.name} style={S.apiKeyCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#E6EDF3', fontSize: 14, fontWeight: 600 }}>{k.label}</span>
              <span style={{
                padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                backgroundColor: k.required ? 'rgba(76,175,80,0.15)' : '#161B22',
                color: k.required ? '#4CAF50' : '#8B949E',
              }}>
                {k.required ? '필수' : '선택'}
              </span>
            </div>
            <div style={{ color: '#8B949E', fontSize: 12, marginBottom: 6 }}>{k.desc}</div>
            <code style={{ color: '#4CAF50', fontSize: 12, backgroundColor: '#0D1117', padding: '4px 8px', borderRadius: 6 }}>
              {k.name}
            </code>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════ 스타일 ═══════ */
const S = {
  h1: { color: '#E6EDF3', fontSize: 24, fontWeight: 700, marginBottom: 4 },
  desc: { color: '#8B949E', fontSize: 14, marginBottom: 24 },
  center: { display: 'flex', justifyContent: 'center', padding: '60px 0' },

  errorBox: { backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  errorClose: { background: 'none', border: 'none', color: '#EF4444', fontSize: 16, cursor: 'pointer' },

  /* 탭 */
  tabBar: { display: 'flex', gap: 8, marginBottom: 24 },
  tabBtn: { padding: '10px 24px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 10, color: '#8B949E', fontSize: 14, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif", fontWeight: 500 },
  tabBtnActive: { borderColor: '#4CAF50', color: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)' },

  /* 사용자 테이블 */
  addForm: { display: 'flex', gap: 8, marginBottom: 16, padding: 16, backgroundColor: '#1E2732', borderRadius: 12, border: '1px solid #2A3441' },
  table: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', overflow: 'hidden' },
  tableHeader: { display: 'flex', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid #2A3441', color: '#8B949E', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' },
  tableRow: { display: 'flex', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #1a2230', color: '#C9D1D9', fontSize: 14, gap: 8 },
  roleBadge: { padding: '3px 10px', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 600 },

  /* 인풋 */
  input: { padding: '8px 12px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, color: '#E6EDF3', fontSize: 13, fontFamily: "'Noto Sans KR',sans-serif", outline: 'none' },
  select: { padding: '8px 12px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, color: '#E6EDF3', fontSize: 13, fontFamily: "'Noto Sans KR',sans-serif" },

  /* 시스템 모니터링 */
  sectionTitle: { color: '#E6EDF3', fontSize: 16, fontWeight: 600, margin: '24px 0 12px' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 8 },
  statCard: { backgroundColor: '#1E2732', borderRadius: 12, border: '1px solid #2A3441', padding: 20, textAlign: 'center' },
  statLabel: { color: '#8B949E', fontSize: 12, marginBottom: 8 },
  statValue: { color: '#E6EDF3', fontSize: 20, fontWeight: 700 },

  card: { backgroundColor: '#1E2732', borderRadius: 12, border: '1px solid #2A3441', padding: 20, marginBottom: 12 },
  barLabel: { display: 'flex', justifyContent: 'space-between', color: '#C9D1D9', fontSize: 13, marginBottom: 8 },
  barWrap: { height: 8, backgroundColor: '#0D1117', borderRadius: 4, overflow: 'hidden', marginBottom: 8 },
  barFill: { height: '100%', backgroundColor: '#4CAF50', borderRadius: 4, transition: 'width 0.5s' },
  barFooter: { display: 'flex', justifyContent: 'space-between', color: '#8B949E', fontSize: 12 },

  /* API 키 */
  apiKeyCard: { backgroundColor: '#1E2732', borderRadius: 12, border: '1px solid #2A3441', padding: 16 },

  /* 버튼 */
  primaryBtn: { padding: '10px 20px', backgroundColor: '#2E7D32', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  ghostBtn: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid #2A3441', borderRadius: 10, color: '#8B949E', fontSize: 13, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  smallBtn: { padding: '6px 12px', backgroundColor: '#161B22', border: '1px solid #2A3441', borderRadius: 8, color: '#C9D1D9', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  smallBtnGreen: { padding: '6px 12px', backgroundColor: '#2E7D32', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  smallBtnDanger: { padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#EF4444', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },

  spinner: { width: 48, height: 48, border: '4px solid #2A3441', borderTop: '4px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
